/**
 * Group day itinerary items by city for multi-city same-day headers.
 * Day View is typically single-city per route; this supports future multi-city days.
 */

import type { ItineraryItem } from '../types/client';

export interface DayCitySection {
  cityId: string;
  cityName: string;
  items: ItineraryItem[];
}

export interface DayWithCityItems {
  cityId?: string | null;
  items?: ItineraryItem[] | null;
}

/**
 * Groups day rows (same calendar date, possibly different cityIds) into city sections.
 * Empty cityId collapses into a single "unknown" bucket.
 */
export function groupDayItemsByCity(
  days: DayWithCityItems[],
  cityNames: Record<string, string> | null | undefined,
  unknownCityLabel: string
): DayCitySection[] {
  const names = cityNames ?? {};
  const order: string[] = [];
  const map = new Map<string, DayCitySection>();

  for (const day of days) {
    const cityId = String(day.cityId ?? '').trim() || '';
    const key = cityId || '__none__';
    let section = map.get(key);
    if (!section) {
      const cityName =
        (cityId && names[cityId]?.trim()) ||
        (cityId
          ? Object.entries(names).find(([id]) => id.toLowerCase() === cityId.toLowerCase())?.[1]?.trim()
          : '') ||
        unknownCityLabel;
      section = { cityId: cityId || key, cityName, items: [] };
      map.set(key, section);
      order.push(key);
    }
    for (const item of day.items ?? []) {
      section.items.push(item);
    }
  }

  return order.map((key) => map.get(key)!);
}

/** True when the UI should render city section headers (more than one city). */
export function shouldShowCitySectionHeaders(sections: DayCitySection[]): boolean {
  if (sections.length <= 1) return false;
  const names = new Set(sections.map((s) => s.cityName.trim().toLowerCase()).filter(Boolean));
  return names.size > 1 || sections.length > 1;
}
